import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, unwrapArray } from "@/lib/api";
import { formatDateTime } from "@/lib/dateTime";
import { StatusPill, type Tone } from "@/components/patterns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export type CampaignRow = {
  id: string; name: string; template_name: string; status: string; created_on: string; created_by: string | null;
  first_send_at: string | null; follow_ups?: number; planned: number; skipped: number; progress: number;
  counts: { queued: number; sent: number; delivered: number; read: number; failed: number; cancelled: number; replied: number };
};

export const CAMPAIGN_STATUS: Record<string, { label: string; tone: Tone }> = {
  BUILDING: { label: "Preparing", tone: "info" },
  SCHEDULED: { label: "Scheduled", tone: "neutral" },
  RUNNING: { label: "Sending", tone: "primary" },
  DONE: { label: "Done", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "warning" },
  FAILED: { label: "Failed", tone: "danger" },
};

export function ProgressBar({ value }: { value: number }) {
  return (
    <div className="h-1.5 w-28 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

/** WhatsApp → Campaigns: every campaign you can see, with how far it got */
export function CampaignsTab() {
  const [rows, setRows] = useState<CampaignRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => api.get("/api/WhatsAppCampaign/List").then((r) => { setRows(unwrapArray<CampaignRow>(r)); setFailed(false); }).catch(() => setFailed(true)), []);
  useEffect(() => { void load(); }, [load]);
  // Campaigns that are still going refresh by themselves
  useEffect(() => {
    if (!rows?.some((r) => ["BUILDING", "SCHEDULED", "RUNNING"].includes(r.status))) return;
    const t = setInterval(() => void load(), 15000);
    return () => clearInterval(t);
  }, [rows, load]);

  if (failed && !rows) return <p className="p-4 text-sm text-muted-foreground">Couldn't load the campaigns. Try again in a moment.</p>;
  if (!rows) return <div className="flex justify-center py-10"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  if (!rows.length) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        <p className="font-medium text-foreground">No campaigns yet</p>
        <p className="mt-1">Open Leads, filter the cold leads, tick them, and choose <strong>WhatsApp campaign</strong> in the bar that appears.</p>
        <Link to="/leads" className="mt-3 inline-block text-primary hover:underline">Go to Leads</Link>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campaign</TableHead><TableHead>Status</TableHead><TableHead>Progress</TableHead>
            <TableHead className="text-right">Sent</TableHead><TableHead className="text-right">Delivered</TableHead><TableHead className="text-right">Read</TableHead><TableHead className="text-right">Replied</TableHead><TableHead className="text-right">Failed</TableHead>
            <TableHead>Started</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((c) => {
            const st = CAMPAIGN_STATUS[c.status] || { label: c.status, tone: "neutral" as Tone };
            return (
              <TableRow key={c.id}>
                <TableCell>
                  <Link to={`/whatsapp/campaigns/${c.id}`} className="font-medium text-primary hover:underline">{c.name}</Link>
                  <div className="text-xs text-muted-foreground">{c.template_name}{c.created_by ? ` · ${c.created_by}` : ""}</div>
                </TableCell>
                <TableCell><StatusPill tone={st.tone}>{st.label}</StatusPill>{!!c.follow_ups && ["RUNNING", "SCHEDULED"].includes(c.status) && <div className="mt-1 text-xs text-muted-foreground">+{c.follow_ups} follow-up{c.follow_ups === 1 ? "" : "s"}</div>}</TableCell>
                <TableCell><ProgressBar value={c.progress} /><div className="mt-1 text-xs text-muted-foreground">{c.counts.sent} of {c.planned}{c.skipped ? ` · ${c.skipped} skipped` : ""}</div></TableCell>
                <TableCell className="text-right tabular-nums">{c.counts.sent}</TableCell>
                <TableCell className="text-right tabular-nums">{c.counts.delivered}</TableCell>
                <TableCell className="text-right tabular-nums">{c.counts.read}</TableCell>
                <TableCell className="text-right tabular-nums">{c.counts.replied}</TableCell>
                <TableCell className="text-right tabular-nums">{c.counts.failed}</TableCell>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(c.first_send_at || c.created_on)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
